import type { ControllerRenderProps, FieldValues } from 'react-hook-form';
import React, { memo, useCallback, useMemo } from 'react';
import { SearchFieldInput, SearchTableFilterInput } from '@/components/custom/arito/form/search-field-input';
import type { TabItem } from '@/components/custom/arito/form/shared-types';
import AddEditActionFormButtons from '../hooks/AddEditActionFormButtons';
import type { ActionButtonFormType } from '../hooks/ActionButtonFormMap';
import { SearchTable } from '@/components/custom/arito/search-table';
import { AritoDialog } from '@/components/custom/arito/dialog';
import { AritoIcon } from '@/components/custom/arito/icon';
import { Button } from '@/components/ui/button';
import { useForm } from '../hooks/useForm';
import { ModalForm } from './ModalForm';
import { cn } from '@/lib/utils';

interface SearchFieldProps {
  field: ControllerRenderProps<FieldValues, string>;
  label: string | undefined;
  disabled?: boolean;
  fieldId: string;
  type: string;
  searchEndpoint?: string;
  searchColumns?: any[];
  searchResultLabelKey?: string;
  searchResultValueKey?: string;
  defaultSearchColumn?: string;
  displayRelatedField: boolean;
  relatedFieldValue: string;
  setRelatedFieldValue: (value: string) => void;
  className?: string;
  dialogTitle?: string;
  hideRowNumberColumn?: boolean;
  actionButtons?: ActionButtonFormType[];
  headerFields?: React.ReactNode;
  tabs?: React.ReactNode[] | TabItem[] | undefined;
  searchDialogOpen: boolean;
  setSearchDialogOpen: (open: boolean) => void;
  searchQuery: string;
  searchResults: any[];
  selectedSearchResult: any;
  setSelectedSearchResult: (result: any) => void;
  displayText: string;
  setDisplayText: (text: string) => void;
  isFullScreen: boolean;
  handleSearchClick: () => void;
  handleSearchQueryChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectSearchResult: (result: any, onChange: any) => void;
  handleConfirmSelection: (onChange: any) => void;
  toggleFullScreen: () => void;
  placeholder?: string;
}

export const SearchField = memo(
  ({
    field,
    label,
    disabled,
    fieldId,
    type,
    searchEndpoint,
    searchColumns,
    searchResultLabelKey,
    searchResultValueKey,
    relatedFieldValue,
    setRelatedFieldValue,
    className,
    dialogTitle,
    actionButtons,
    headerFields,
    tabs,
    searchDialogOpen,
    setSearchDialogOpen,
    searchQuery,
    searchResults,
    selectedSearchResult,
    setSelectedSearchResult,
    displayText,
    setDisplayText,
    isFullScreen,
    handleSearchClick,
    handleSearchQueryChange,
    handleSelectSearchResult,
    handleConfirmSelection,
    toggleFullScreen,
    placeholder
  }: SearchFieldProps) => {
    const { showForm, formMode, currentObj, handleCloseForm, handleOpenAddForm, handleOpenEditForm } = useForm(
      {},
      'add'
    );

    const title = useMemo(() => dialogTitle || `Danh mục ${String(label).toLowerCase()}`, [dialogTitle, label]);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = e.target.value;
        field.onChange(nextValue);
        setDisplayText(nextValue);
        setRelatedFieldValue('');
      },
      [field, setDisplayText, setRelatedFieldValue]
    );

    const handleOpenDialog = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        handleSearchClick();
      },
      [handleSearchClick]
    );

    const handleConfirmClick = useCallback(() => {
      handleConfirmSelection(field.onChange);
    }, [field, handleConfirmSelection]);

    const handleCloseDialog = useCallback(() => {
      setSearchDialogOpen(false);
    }, [setSearchDialogOpen]);

    const handleEditClick = useCallback(() => {
      handleOpenEditForm(selectedSearchResult);
    }, [handleOpenEditForm, selectedSearchResult]);

    const handleResultClick = useCallback(
      (result: any) => {
        setSelectedSearchResult(result);
      },
      [setSelectedSearchResult]
    );

    const handleResultDoubleClick = useCallback(
      (result: any) => {
        handleSelectSearchResult(result, field.onChange);
      },
      [field, handleSelectSearchResult]
    );

    const dialogActions = useMemo(
      () => (
        <>
          <Button
            type='button'
            onClick={handleConfirmClick}
            disabled={!selectedSearchResult}
            className='h-[var(--erp-command-button-height)] rounded-none bg-[#2563eb] px-4 text-white hover:bg-[#1d4ed8]'
          >
            <AritoIcon icon={884} className='mx-1' />
            Đồng ý
          </Button>
          <Button
            type='button'
            onClick={handleCloseDialog}
            variant='outline'
            className='h-[var(--erp-command-button-height)] rounded-none border-[#00897b] px-4 text-[#00897b] hover:bg-gray-100 hover:text-[#00897b]'
          >
            <AritoIcon icon={885} className='mx-1' />
            Hủy
          </Button>
        </>
      ),
      [handleCloseDialog, handleConfirmClick, selectedSearchResult]
    );

    return (
      <div className={cn('relative flex w-32 flex-shrink-0 items-center', className)}>
        <SearchFieldInput
          id={fieldId}
          name={field.name}
          inputRef={field.ref}
          onBlur={field.onBlur}
          type={type}
          disabled={disabled}
          value={displayText || field.value || ''}
          onChange={handleInputChange}
          placeholder={disabled ? undefined : placeholder}
          onSearchClick={handleOpenDialog}
          className='w-full min-w-0'
        />

        {relatedFieldValue && (
          <div className='ml-0 mt-1 max-w-full flex-grow overflow-hidden text-ellipsis whitespace-nowrap text-left text-sm text-gray-700 sm:ml-3 sm:mt-0 sm:max-w-[65%]'>
            {relatedFieldValue}
          </div>
        )}

        <AritoDialog
          open={searchDialogOpen}
          onClose={() => {}}
          disableBackdropClose={true}
          disableEscapeKeyDown={true}
          fullScreen={isFullScreen}
          onFullscreenToggle={toggleFullScreen}
          title={title}
          titleIcon={<AritoIcon icon={584} className='mx-2' />}
          actions={dialogActions}
        >
          {!searchEndpoint && (
            <div className='px-3 py-2 text-red-500'>
              Vui lòng cung cấp searchEndpoint để sử dụng chức năng tìm kiếm.
            </div>
          )}

          {searchEndpoint && (
            <>
              <div className='flex items-center border-b border-gray-200 px-4 py-2'>
                <SearchTableFilterInput value={searchQuery} onChange={handleSearchQueryChange} />
                {actionButtons && (
                  <AddEditActionFormButtons
                    onAddForm={handleOpenAddForm}
                    onEditForm={handleEditClick}
                    errorMessage=''
                    tableActionButtonsForm={actionButtons}
                  />
                )}
              </div>

              <SearchTable
                searchResults={searchResults}
                searchColumns={searchColumns}
                searchResultLabelKey={searchResultLabelKey}
                searchResultValueKey={searchResultValueKey}
                selectedResult={selectedSearchResult}
                onResultClick={handleResultClick}
                onResultDoubleClick={handleResultDoubleClick}
              />
            </>
          )}

          {showForm && (
            <ModalForm
              title={title}
              headerFields={headerFields || <></>}
              tabs={tabs || []}
              open={showForm}
              onClose={handleCloseForm}
              formMode={formMode}
              initialData={currentObj || {}}
              handleSubmit={() => {}}
            />
          )}
        </AritoDialog>
      </div>
    );
  }
);

SearchField.displayName = 'SearchField';
